import { CORPORATION_REQUIRED_FIELDS } from "src/core/constants/corporation-fields";
import { EI_REQUIRED_FIELDS } from "src/core/constants/ei-fields";
import { EReputationCategory } from "src/core/constants/eReputation";
import {
  FolderFileDocumentCode,
  MappedStakeholder,
  PartialFolderStakeholder,
  StakeholderRoleEnum,
  StakeholderType,
} from "src/core/constants/folder";
import { IRiskOption } from "src/core/constants/options";
import { CustomRiskLevel, getRiskLevelValue } from "src/core/constants/risk";
import { STAKEHOLDER_REQUIRED_FIELDS } from "src/core/constants/stakeholder-fields";
import { UpdateStakeholderFullFormValues } from "src/core/forms/stakeholder/updateStakeholderFull.schema";
import { DocumentStatus, FolderFileQuery } from "src/core/gql/graphql";
import { determineRiskLevel, mapReputationStatus } from "src/core/mappers/entity.mapper";
import { calculateCorporationRiskLevel } from "src/core/utils/folderAnalysis";

/**
 * Transforme un tableau de stakeholders issus d'une API GraphQL en objets métier.
 *
 * Cette fonction extrait et reformate les informations utiles pour chaque stakeholder, en incluant :
 * - l'identifiant et le type d'entité (Person ou Corporation),
 * - les champs saisis (comme le prénom, le nom ou la raison sociale),
 * - la liste des documents attendus et leur statut de dépôt,
 * - les rôles métier (représentant légal, bénéficiaire effectif direct) en fonction des indicateurs booléens fournis.
 */
export const mapToMappedStakeholders = (
  raw: PartialFolderStakeholder[],
  countryWithRiskOptions: IRiskOption[]
): MappedStakeholder[] => {
  if (!Array.isArray(raw)) return [];

  return raw.map((stakeholder): MappedStakeholder => {
    const type = stakeholder.isFolderCustomer
      ? StakeholderType.Corporation
      : StakeholderType.Person;
    const fields: Record<string, string> = {};

    const roles: StakeholderRoleEnum[] = [];
    if (stakeholder.beneficiaryOwner) {
      roles.push(StakeholderRoleEnum.direct_beneficiary_owner);
    }
    if (stakeholder.isLegalRepresentative) {
      roles.push(StakeholderRoleEnum.legal_representative);
    }

    stakeholder.inputValues.forEach((val) => {
      if (val.inputField?.fieldCode && val.stringValue !== undefined && val.stringValue !== null) {
        fields[val.inputField.fieldCode] = val.stringValue;
      }
    });

    const documents = stakeholder.supportingDocuments.map((doc) => {
      // On suppose que doc est bien du type ExpectedDocument généré par GraphQL
      return {
        code: doc.mainDocument?.type?.code ?? "",
        name: doc.mainDocument?.type?.name ?? "",
        uploaded: !!doc.mainDocument,
        status: doc.mainDocument?.__typename === "Document" ? doc.mainDocument.status : undefined,
      };
    });

    // Nombre de champs du formulaire requis manquants
    const missingRequiredFieldCount = STAKEHOLDER_REQUIRED_FIELDS.reduce((count, field) => {
      return !fields[field] ? count + 1 : count;
    }, 0);

    // Nombre de documents manquants
    const missingDocumentCount = stakeholder.supportingDocuments.filter(
      (document) =>
        document.mainDocument === null ||
        (document.mainDocument?.__typename === "Document" &&
          document.mainDocument.status === DocumentStatus.Expired)
    ).length;

    // Niveau de risque du stakeholder, se base uniquement sur le niveau de risque du pays de résidence.
    const riskLevel = fields.residenceCountryCode
      ? countryWithRiskOptions.find((c) => c.value === fields.residenceCountryCode)?.risk
      : "UNDEFINED";

    // se base uniquement sur le niveau de risque du pays de résidence.
    return {
      id: stakeholder.entity.id,
      type,
      riskLevel: riskLevel,
      roles,
      isFolderCustomer: stakeholder.isFolderCustomer,
      isLegalRepresentative: stakeholder.isLegalRepresentative,
      isBeneficiaryOwner: stakeholder.beneficiaryOwner,
      title: fields.title,
      firstName: fields.firstName,
      birthName: fields.birthName,
      lastName: fields.lastName,
      citizenshipCountryCode: fields.citizenshipCountryCode,
      birthDate: fields.birthDate,
      residenceCountryCode: fields.residenceCountryCode,
      birthCountryCode: fields.birthCountryCode,
      birthPlace: fields.birthPlace,
      socialReason: fields.name,
      documents,
      missingRequiredFieldCount,
      missingDocumentCount,
      // On fait ça parce que c'est un Booléen stringifié en base de données.
      duplicateCheckDone: fields.customBoolean1 === "true",
      duplicateCheckLoading: true,
      duplicateCheckFetched: false,
    };
  });
};

/**
 * Transforme un tableau de stakeholders issus d'une API GraphQL en objets métier.
 *
 * Cette fonction extrait et reformate les informations utiles pour chaque stakeholder, en incluant :
 * - l'identifiant et le type d'entité (Person ou Corporation),
 * - les champs saisis (comme le prénom, le nom ou la raison sociale),
 * - la liste des documents attendus et leur statut de dépôt,
 * - les rôles métier (représentant légal, bénéficiaire effectif direct) en fonction des indicateurs booléens fournis.
 * - la raison sociale pour les entreprises.
 * - les alertes PEP et sanctions.
 */
export const mapToMappedStakeholdersWithAlertes = (
  raw: PartialFolderStakeholder[],
  countryWithRiskOptions: IRiskOption[],
  activityWithRiskOptions: IRiskOption[],
  legalTypesWithRiskOptions: IRiskOption[],
  folderFile: FolderFileQuery,
  isPPH = false
): MappedStakeholder[] => {
  if (!Array.isArray(raw)) return [];
  const orderedStakeholders = [...raw].sort((a, b) => {
    if (a.isFolderCustomer && !b.isFolderCustomer) {
      return -1;
    }
    if (!a.isFolderCustomer && b.isFolderCustomer) {
      return 1;
    }
    return 0;
  });
  return orderedStakeholders.map((stakeholder): MappedStakeholder => {
    const type = isPPH
      ? StakeholderType.Entrepreneur
      : stakeholder.isFolderCustomer
      ? StakeholderType.Corporation
      : StakeholderType.Person;
    const fields: Record<string, string> = {};

    const roles: StakeholderRoleEnum[] = [];
    if (stakeholder.beneficiaryOwner) {
      roles.push(StakeholderRoleEnum.direct_beneficiary_owner);
    }
    if (stakeholder.isLegalRepresentative) {
      roles.push(StakeholderRoleEnum.legal_representative);
    }

    stakeholder.inputValues.forEach((val) => {
      if (val.inputField?.fieldCode && val.stringValue !== undefined && val.stringValue !== null) {
        fields[val.inputField.fieldCode] = val.stringValue;
      }
    });
    const documents = stakeholder.supportingDocuments
      .filter(
        (doc) =>
          doc.mainDocument?.type?.code !== FolderFileDocumentCode.MISSION_LETTER &&
          doc.abstractDocumentType?.code !== FolderFileDocumentCode.MISSION_LETTER
      )
      .map((doc) => {
        // On suppose que doc est bien du type ExpectedDocument généré par GraphQL
        return {
          code: doc.mainDocument?.type?.code ?? "",
          name: doc.mainDocument?.type?.name ?? "",
          uploaded: !!doc.mainDocument,
          id: doc.mainDocument?.id ?? null,
          fileName: doc.mainDocument?.__typename === "Document" ? doc.mainDocument.name : null,
          type: {
            name: doc.abstractDocumentType.name,
            code: doc.abstractDocumentType.code,
          },
          externalDocument:
            doc?.mainDocument?.__typename === "ExternalDocument" &&
            doc.mainDocument.id !== undefined,
          status: doc.mainDocument?.__typename === "Document" ? doc.mainDocument.status : undefined,
        };
      });
    const fieldsRequired =
      type === StakeholderType.Person
        ? STAKEHOLDER_REQUIRED_FIELDS
        : type === StakeholderType.Entrepreneur
        ? EI_REQUIRED_FIELDS
        : CORPORATION_REQUIRED_FIELDS;
    // Nombre de champs du formulaire requis manquants
    const missingRequiredFieldCount = fieldsRequired.reduce((count, field) => {
      return !fields[field] ? count + 1 : count;
    }, 0);

    // Nombre de documents manquants
    const missingDocumentCount = stakeholder.supportingDocuments.filter(
      (document) =>
        (document.mainDocument === null ||
          (document.mainDocument?.__typename === "Document" &&
            document.mainDocument.status === DocumentStatus.Expired)) &&
        document.abstractDocumentType?.code !== FolderFileDocumentCode.MISSION_LETTER
    ).length;
    let riskLevelDefault = CustomRiskLevel.UNDEFINED;
    if (stakeholder.isFolderCustomer && !isPPH) {
      riskLevelDefault = calculateCorporationRiskLevel(
        folderFile,
        countryWithRiskOptions,
        activityWithRiskOptions,
        legalTypesWithRiskOptions
      );
    } else {
      // Niveau de risque du stakeholder, se base uniquement sur le niveau de risque du pays de résidence.
      riskLevelDefault =
        countryWithRiskOptions.find((c) => c.value === fields.residenceCountryCode)?.risk ??
        CustomRiskLevel.UNDEFINED;
    }

    if (isPPH) {
      riskLevelDefault = CustomRiskLevel.UNDEFINED;
    }
    const eReputation = stakeholder.entity.eReputation;

    const pepEntry = eReputation?.entries.find(
      (entry) => entry.category.code === EReputationCategory.PEP
    );
    const sanctionEntry = eReputation?.entries.find(
      (entry) => entry.category.code === EReputationCategory.SANCTION
    );

    const pepStatus = mapReputationStatus(pepEntry);
    const sanctionStatus = mapReputationStatus(sanctionEntry);
    const riskLevelEReput = determineRiskLevel(pepStatus, sanctionStatus);
    const riskLevel =
      getRiskLevelValue(riskLevelDefault) > getRiskLevelValue(riskLevelEReput)
        ? riskLevelDefault
        : riskLevelEReput;

    // se base uniquement sur le niveau de risque du pays de résidence.
    return {
      id: stakeholder.entity.id,
      type,
      riskLevel: riskLevel,
      roles,
      isFolderCustomer: stakeholder.isFolderCustomer,
      isLegalRepresentative: stakeholder.isLegalRepresentative,
      isBeneficiaryOwner: stakeholder.beneficiaryOwner,
      title: fields.title,
      firstName: fields.firstName,
      birthName: fields.birthName,
      lastName: fields.lastName,
      citizenshipCountryCode: fields.citizenshipCountryCode,
      birthDate: fields.birthDate,
      residenceCountryCode: fields.residenceCountryCode,
      birthCountryCode: fields.birthCountryCode,
      birthPlace: fields.birthPlace,
      socialReason: fields.name,
      documents,
      missingRequiredFieldCount,
      missingDocumentCount,
      pepStatus,
      sanctionStatus,
      // On fait ça parce que c'est un Booléen stringifié en base de données.
      duplicateCheckDone: fields.customBoolean1 === "true",
      duplicateCheckLoading: true,
      duplicateCheckFetched: false,
    };
  });
};

export const mapFormValuesToMappedStakeholder = (
  id: string,
  formValues: UpdateStakeholderFullFormValues,
  current: Partial<MappedStakeholder>
): MappedStakeholder => {
  let missingRequiredFieldCount = 0;
  STAKEHOLDER_REQUIRED_FIELDS.forEach((field) => {
    if (!formValues[field as keyof UpdateStakeholderFullFormValues]) {
      missingRequiredFieldCount++;
    }
  });

  const missingDocumentCount = Object.values(formValues.documents).filter(
    (doc) => !doc.file && !doc.fileId && !doc.existing
  ).length;

  return {
    id,
    type: StakeholderType.Person,
    riskLevel: undefined,
    roles: formValues.roles?.map((role) => role as StakeholderRoleEnum) ?? [],
    isFolderCustomer: false,
    isLegalRepresentative:
      formValues.roles?.includes(StakeholderRoleEnum.legal_representative) ?? false,
    isBeneficiaryOwner:
      formValues.roles?.includes(StakeholderRoleEnum.direct_beneficiary_owner) ?? false,
    title: formValues.title,
    firstName: formValues.firstName,
    birthName: formValues.birthName,
    lastName: formValues.lastName,
    citizenshipCountryCode: formValues.citizenshipCountryCode,
    birthDate: formValues.birthDate,
    residenceCountryCode: formValues.residenceCountryCode,
    birthCountryCode: formValues.birthCountryCode,
    birthPlace: formValues.birthPlace,
    documents: current.documents ?? [],
    missingRequiredFieldCount,
    missingDocumentCount: missingDocumentCount ?? 0,

    // Dans le cas d'un nouveau stakeholder
    duplicateCheckDone: false,
    duplicateCheckLoading: true,
    duplicateCheckFetched: false,
  };
};
